import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { ACCENT, PrimaryButton, SecondaryButton, inputClass, ringStyle, clean } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { MESSAGES, fieldErrors, trackApi, trackErrorMessage } from "./trackApi";
import WithdrawLicensingDialog from "./WithdrawLicensingDialog";

// Artist Studio > Monetization: licensing prices per usage class (Personal, Creator, Business) for reuse outside
// Pipaan. Streaming on Pipaan is paid separately (subscriptions and royalties) and is not changed by anything here.
export const CLASSES = [
  { key: "personal", title: "Personal", blurb: "Private, non-monetised use by an ordinary fan account.", terms: [{ key: "price", label: "Price" }] },
  {
    key: "creator",
    title: "Creator",
    blurb: "Accounts earning from their content — a licence is needed to use this track in monetised content.",
    terms: [{ key: "one_year", label: "Annual / term" }, { key: "lifetime", label: "Lifetime" }],
  },
  {
    key: "business",
    title: "Business",
    blurb: "Brands and commercial advertisers — a Business licence is needed for promotional use.",
    terms: [{ key: "one_year", label: "Annual / term" }, { key: "lifetime", label: "Lifetime" }],
  },
];
// Personal licences are always free (Free or Unavailable, no price). Creator and Business licences can never be free
// (Paid inside the admin's limits, or Unavailable).
const MODES = [
  { key: "free", label: "Free" },
  { key: "paid", label: "Paid" },
  { key: "unavailable", label: "Unavailable" },
];
const modesFor = (cls) => MODES.filter((m) => (cls === "personal" ? m.key !== "paid" : m.key !== "free"));
const DEFAULT_RULES = { min_price: 1, max_price: 100000 };

const toText = (v) => (v === null || v === undefined ? "" : String(v));
export const formatMoney = (n) => `$${Number(n) % 1 === 0 ? Number(n) : Number(n).toFixed(2)}`;

// "" means no price entered. Returns { value } or { error }. A price must be a non-negative amount with at most two
// decimals, inside the platform minimum and maximum.
export function parsePrice(text, label, rules = DEFAULT_RULES) {
  const t = String(text ?? "").trim();
  if (t === "") return { value: null };
  if (/^-/.test(t)) return { error: `${label} cannot be negative.` };
  if (!/^\d+(\.\d{1,2})?$/.test(t)) {
    return { error: /^\d+\.\d{3,}$/.test(t) ? `${label} can have at most two decimal places.` : `${label} must be a number.` };
  }
  const n = Number(t);
  if (n < rules.min_price) return { error: `${label} must be at least ${formatMoney(rules.min_price)}. Choose Unavailable if you do not want to offer it.` };
  if (n > rules.max_price) return { error: `${label} cannot be more than ${formatMoney(rules.max_price)}.` };
  return { value: n };
}

// Checks one track's form and builds the request. Each class is checked on its own and annual and lifetime are
// validated independently. Only classes whose settings are valid-and-changed need to be sent, but all three are sent
// so the saved configuration matches what the artist sees. Errors are keyed like "creator.lifetime".
export function buildLicensingBody(form, trackId, rules = DEFAULT_RULES) {
  const errors = {};
  const body = { track_id: trackId };
  CLASSES.forEach(({ key, title, terms }) => {
    const value = form[key];
    const out = { mode: value.mode };
    if (key !== "personal" && value.mode === "paid") {
      let entered = 0;
      terms.forEach(({ key: term, label }) => {
        const name = `${title} ${label.toLowerCase()} price`;
        const r = parsePrice(value[term], name, rules);
        const errKey = `${key}.${term}`;
        if (r.error) errors[errKey] = r.error;
        else if (r.value !== null) entered += 1;
        out[term] = r.error ? null : r.value;
      });
      if (!entered && !terms.some(({ key: term }) => errors[`${key}.${term}`])) {
        errors[`${key}.one_year`] = `Enter an annual or a lifetime ${title} price, or choose Unavailable.`;
      }
    }
    body[key] = out;
  });
  // A lifetime price is never below the annual price, and Business is never below Creator for the same term (the API checks the
  // same rules). Only prices that passed the checks above are compared.
  ["creator", "business"].forEach((cls) => {
    const o = body[cls];
    if (o.mode === "paid" && o.one_year != null && o.lifetime != null && o.lifetime < o.one_year && !errors[`${cls}.lifetime`]) {
      errors[`${cls}.lifetime`] = "Lifetime price must be at least the annual price.";
    }
  });
  ["one_year", "lifetime"].forEach((term) => {
    const c = body.creator;
    const b = body.business;
    if (c.mode === "paid" && b.mode === "paid" && c[term] != null && b[term] != null && b[term] < c[term] && !errors[`business.${term}`]) {
      errors[`business.${term}`] = "Business price must be at least the Creator price.";
    }
  });
  return { body, errors };
}

const formFrom = (row) => {
  // A Creator or Business licence saved as Free earlier shows as Paid with no price, so the artist has to enter one or
  // choose Unavailable. A Personal licence that was offered at any price shows as Free.
  const one = (cls) => {
    const saved = row[cls]?.mode || "unavailable";
    const mode = saved === "free" ? "paid" : saved;
    return { mode, one_year: saved === "free" ? "" : toText(row[cls]?.one_year), lifetime: saved === "free" ? "" : toText(row[cls]?.lifetime) };
  };
  const personalMode = row.personal?.mode;
  return { personal: { mode: personalMode && personalMode !== "unavailable" ? "free" : "unavailable" }, creator: one("creator"), business: one("business") };
};

function PriceCell({ cls, personal }) {
  if (!cls || cls.mode === "unavailable") return <span className="text-gray-500">Unavailable</span>;
  if (cls.mode === "free") return <span className="font-semibold text-gray-900">Free</span>;
  if (personal) return <span className="font-semibold text-gray-900">{cls.price == null ? "—" : formatMoney(cls.price)}</span>;
  return (
    <div>
      <div className="font-semibold text-gray-900">{cls.one_year == null ? "—" : `${formatMoney(cls.one_year)} / yr`}</div>
      <div className="text-xs text-gray-500">{cls.lifetime == null ? "—" : `${formatMoney(cls.lifetime)} lifetime`}</div>
    </div>
  );
}

function rowStatus(row) {
  if (!row.editable) return { label: "Taken down", cls: "bg-gray-200 text-gray-600" };
  if (row.licensing_withdrawn) return { label: "Withdrawn", cls: "bg-gray-200 text-gray-600" };
  if (row.licensing_enabled) return { label: "Monetized", cls: "text-white", style: { backgroundColor: ACCENT } };
  return { label: "Not monetized", cls: "bg-gray-200 text-gray-600" };
}

function ClassForm({ spec, form, errors, rules, disabled, onMode, onPrice }) {
  const value = form[spec.key];
  return (
    <fieldset className="rounded-xl border border-gray-200 p-4" disabled={disabled}>
      <legend className="px-1 font-bold text-gray-900">{spec.title}</legend>
      <p className="text-xs text-gray-500 -mt-1 mb-3">{spec.blurb}</p>
      <div className="flex flex-wrap gap-4" role="radiogroup" aria-label={`${spec.title} availability`}>
        {modesFor(spec.key).map((m) => (
          <label key={m.key} className="flex items-center gap-1.5 text-sm text-gray-800">
            <input type="radio" name={`${spec.key}-mode`} value={m.key} checked={value.mode === m.key} onChange={() => onMode(spec.key, m.key)} aria-label={`${spec.title} ${m.label}`} />
            {m.label}
          </label>
        ))}
      </div>
      {value.mode === "paid" && (
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          {spec.terms.map(({ key: term, label }) => {
            const errKey = `${spec.key}.${term}`;
            return (
              <div key={term}>
                <label className="block text-xs font-semibold text-gray-700 mb-1">{label} (USD)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className={`${inputClass(errors[errKey])} !py-2 text-sm`}
                  style={ringStyle}
                  aria-label={`${spec.title} ${label} price`}
                  placeholder={`min ${formatMoney(rules.min_price)}`}
                  value={value[term]}
                  onChange={(e) => onPrice(spec.key, term, clean.decimal(e.target.value))}
                />
                {errors[errKey] && <p className="text-red-600 text-xs mt-1">{errors[errKey]}</p>}
              </div>
            );
          })}
        </div>
      )}
      {value.mode === "free" && <p className="text-xs text-gray-500 mt-3">Personal licences are always free for private, non-commercial use.</p>}
      {value.mode === "unavailable" && <p className="text-xs text-gray-500 mt-3">New {spec.title} licences cannot be bought. Licences already sold stay valid.</p>}
    </fieldset>
  );
}

function PricingDialog({ row, rules, onCancel, onSaved }) {
  const [form, setForm] = useState(() => formFrom(row));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const setMode = (cls, mode) => {
    setForm((f) => ({ ...f, [cls]: { ...f[cls], mode } }));
    setErrors((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith(`${cls}.`))));
  };
  const setPrice = (cls, term, value) => {
    setForm((f) => ({ ...f, [cls]: { ...f[cls], [term]: value } }));
    setErrors((e) => ({ ...e, [cls === "personal" ? "personal.price" : `${cls}.${term}`]: undefined }));
  };

  const save = async () => {
    if (saving) return;
    const { body, errors: found } = buildLicensingBody(form, row.track_id, rules);
    setErrors(found);
    if (Object.keys(found).length) return;
    setSaving(true);
    try {
      onSaved(await trackApi.saveLicensing(body));
      toast.success(`Pricing saved for “${row.title}”.`);
    } catch (e) {
      const fields = fieldErrors(e);
      if (fields) setErrors(fields);
      // The previously saved prices are still in place; the dialog stays open so nothing typed is lost.
      toast.error(`${trackErrorMessage(e, MESSAGES[500])} Your previous pricing is unchanged.`);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center bg-black/40 px-4 py-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="pricing-title" data-testid="pricing-dialog">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-7 shadow-xl my-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="pricing-title" className="text-2xl font-bold text-gray-900">Licensing prices</h2>
            <p className="text-gray-700 font-semibold mt-1">{row.title || "Untitled"} <span className="text-gray-400 font-normal">· Track #{row.track_id}</span></p>
          </div>
          <button type="button" onClick={onCancel} aria-label="Close" className="text-gray-500 text-2xl leading-none">×</button>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Each usage class has its own setting. New prices apply to new purchases only; licences already bought keep the terms they were bought with.
        </p>
        <div className="space-y-4 mt-5">
          {CLASSES.map((spec) => (
            <ClassForm key={spec.key} spec={spec} form={form} errors={errors} rules={rules} disabled={saving} onMode={setMode} onPrice={setPrice} />
          ))}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <SecondaryButton onClick={onCancel} disabled={saving} data-testid="pricing-cancel">Cancel</SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving} data-testid="save-licensing">{saving ? "Saving…" : "Save Changes"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function MonetizationBody() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusId = Number(params.get("track")) || null;
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [busy, setBusy] = useState(false);
  const opened = useRef(false);

  const load = useCallback(() => {
    setLoadError("");
    trackApi.licensingList().then(
      (result) => setData(result),
      (e) => {
        setLoadError(trackErrorMessage(e, MESSAGES[500]));
        setData({ tracks: [], pricing_rules: DEFAULT_RULES });
      }
    );
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Coming from a track's "Set Pricing" button: open that track's pricing straight away.
  useEffect(() => {
    if (!data || !focusId || opened.current) return;
    opened.current = true;
    const row = data.tracks.find((r) => r.track_id === focusId);
    if (row && row.editable) setEditingId(row.track_id);
  }, [data, focusId]);

  if (data === null) {
    return (
      <div className="flex justify-center py-16">
        <Loading />
      </div>
    );
  }

  const rows = data.tracks || [];
  const rules = { ...DEFAULT_RULES, ...(data.pricing_rules || {}) };
  const editing = rows.find((r) => r.track_id === editingId);
  // A track withdrawn from licensing leaves this list; it comes back when licensing is set again (Edit).
  // FSD 6.4.2: a track is monetized when Creator or Business is offered (a Personal-only track is not), or it has earned money.
  const offersPaidClass = (r) => (r.creator?.mode || "unavailable") !== "unavailable" || (r.business?.mode || "unavailable") !== "unavailable";
  const monetized = rows.filter((r) => !r.licensing_withdrawn && (offersPaidClass(r) || Number(r.earnings?.total) > 0));
  const confirmWithdraw = async () => {
    if (busy || !withdrawing) return;
    setBusy(true);
    try {
      const updated = await trackApi.withdrawLicensing(withdrawing.track_id);
      replaceRow(updated);
      toast.success(`“${withdrawing.title || "The track"}” was withdrawn from licensing. Streaming is not affected.`);
      setWithdrawing(null);
    } catch (e) {
      toast.error(trackErrorMessage(e, MESSAGES[500]));
      // The track may have changed since this page loaded (for example it was taken down); show how it is now.
      if (e?.response?.status === 409) {
        setWithdrawing(null);
        load();
      }
    } finally {
      setBusy(false);
    }
  };
  const replaceRow = (updated) => setData((d) => ({ ...d, tracks: d.tracks.map((r) => (r.track_id === updated.track_id ? { ...r, ...updated } : r)) }));

  return (
    <div className="space-y-6" data-testid="monetization">
      <div className="-mt-2">
        <p className="text-sm text-gray-600">
          Set licensing prices per usage class, or make a track unavailable for licensing. Streaming on Nyawoo itself is covered by listener subscriptions and paid out
          separately as streaming royalties — these settings only control licensing for reuse outside the app.
        </p>
        <Link to="/artist-studio/monetization/usage-log" className="inline-block mt-3 text-sm font-semibold" style={{ color: ACCENT }} data-testid="usage-log-link">
          View entitlement usage log →
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          ["Personal", "Private, non-monetised use by an ordinary fan account."],
          ["Creator", "Accounts earning from their content — needs a licence to use this track in monetised posts."],
          ["Business", "Brand or commercial advertiser — needs a business-class licence for any promotional use."],
        ].map(([title, text]) => (
          <div key={title} className="rounded-xl border border-gray-200 bg-white px-4 py-3">
            <p className="text-sm font-bold text-gray-900">{title}</p>
            <p className="text-xs text-gray-500 mt-1">{text}</p>
          </div>
        ))}
      </div>
      {loadError && (
        <p className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">
          {loadError} <button type="button" className="underline font-semibold" onClick={load}>Try again</button>
        </p>
      )}

      <section className="rounded-2xl border border-gray-200 bg-[#f3f7f4] p-4 overflow-x-auto" aria-label="Licensing by track">
        {rows.length === 0 ? (
          <p className="text-gray-600 p-3">
            No tracks to license yet. Tracks appear here once they are submitted for review.{" "}
            <Link to="/artist-studio/tracks" className="font-semibold underline">Go to your catalogue</Link>
          </p>
        ) : (
          <table className="w-full text-left text-[15px]">
            <thead className="text-xs font-bold uppercase tracking-wide" style={{ color: ACCENT }}>
              <tr>
                <th className="py-2 pr-4">Track</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Personal</th>
                <th className="py-2 pr-4">Creator <span className="normal-case font-normal">(term / lifetime)</span></th>
                <th className="py-2 pr-4">Business <span className="normal-case font-normal">(term / lifetime)</span></th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const status = rowStatus(row);
                return (
                  <tr key={row.track_id} className="border-t border-gray-200 align-top" data-testid={`licensing-row-${row.track_id}`}>
                    <td className="py-3 pr-4 font-semibold text-gray-900">{row.title || "Untitled"}</td>
                    <td className="py-3 pr-4">
                      <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold ${status.cls}`} style={status.style}>{status.label}</span>
                    </td>
                    <td className="py-3 pr-4"><PriceCell cls={row.personal} personal /></td>
                    <td className="py-3 pr-4"><PriceCell cls={row.creator} /></td>
                    <td className="py-3 pr-4"><PriceCell cls={row.business} /></td>
                    <td className="py-3 whitespace-nowrap">
                      {row.editable ? (
                        <button type="button" onClick={() => setEditingId(row.track_id)} className="text-sm font-semibold" style={{ color: ACCENT }} data-testid={`edit-pricing-${row.track_id}`}>
                          Edit
                        </button>
                      ) : (
                        <span className="text-xs text-gray-500">Read-only</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section className="rounded-2xl border border-gray-200 bg-[#f3f7f4] p-6" aria-label="My monetized tracks">
        <h2 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: ACCENT }}>My monetized tracks</h2>
        {monetized.length === 0 ? (
          <p className="text-gray-600">None of your tracks is offered for licensing yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {monetized.map((row) => {
              const e = row.earnings || {};
              return (
                <li key={row.track_id} className="flex flex-wrap items-center justify-between gap-4 py-2.5">
                  <span className="font-semibold text-gray-900">{row.title || "Untitled"}</span>
                  <span className="flex flex-wrap items-center gap-x-6 gap-y-1">
                    <span className="text-gray-700 text-sm">
                      <span className="font-semibold text-gray-900">{formatMoney(e.total || 0)}</span> earned
                      {Number(e.pending) > 0 && <> · {formatMoney(e.pending)} pending</>}
                      {Number(e.withdrawn) > 0 && <> · {formatMoney(e.withdrawn)} withdrawn</>}
                      {" · "}<span className="font-semibold text-gray-900" data-testid={`available-${row.track_id}`}>{formatMoney(e.available || 0)}</span> available
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate("/artist-studio/wallet")}
                      className="text-sm font-semibold"
                      style={{ color: ACCENT }}
                      data-testid={`withdraw-earnings-${row.track_id}`}
                    >
                      Withdraw earnings
                    </button>
                    {row.can_withdraw_licensing && (
                      <button
                        type="button"
                        onClick={() => setWithdrawing(row)}
                        className="text-sm font-semibold"
                        style={{ color: "#c0392b" }}
                        data-testid={`withdraw-${row.track_id}`}
                      >
                        Withdraw from licensing
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
        <p className="text-xs text-gray-500 mt-3">
          Only the available amount can be withdrawn; pending and already withdrawn amounts are not. Licensing earnings are separate from Nyawoo streaming royalties.
          {data.payout?.account_last4 ? ` Payouts go to your bank account ending ${data.payout.account_last4}.` : ""}
        </p>
      </section>

      {withdrawing && (
        <WithdrawLicensingDialog row={withdrawing} busy={busy} onCancel={() => !busy && setWithdrawing(null)} onConfirm={confirmWithdraw} />
      )}
      {editing && (
        <PricingDialog
          row={editing}
          rules={rules}
          onCancel={() => setEditingId(null)}
          onSaved={(updated) => {
            replaceRow(updated);
            setEditingId(null);
          }}
        />
      )}
    </div>
  );
}

export default function Monetization() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="monetization" heading="Monetization" tabs={[]} maxWidth="max-w-6xl">
      <MonetizationBody />
    </StudioGate>
  );
}
